<template>
  <div>
    <!-- Section header -->
    <div class="mb-6">
      <div class="ext-section-number">Quality</div>
      <h1 class="ext-section-title">Quality Gaps</h1>
      <p class="ext-section-desc">Patient-level care gaps sourced from payer quality reports. Each row is a patient × measure. A gap is <strong>open</strong> when the numerator event (screening, lab result, controlled value) has not been observed within the measurement period, and <strong>closed</strong> once it has. Use the Gap Status filter to view open gaps to work, closed gaps already met, or both.</p>
    </div>

    <div v-if="loading" style="padding: 40px; text-align: center; color: #A9A29A; font-size: 13px;">Loading quality gaps…</div>
    <div v-else-if="!allRows.length" style="padding: 40px; text-align: center; color: #A9A29A; font-size: 13px;">No quality gap data available.</div>
    <div v-else>

      <!-- Open vs Closed gap trend (one stacked bar per report date; grows monthly) -->
      <div class="ext-hero qual-chart-card">
        <div class="ext-hero-label" style="margin-bottom:8px;">Gap Closure Trend{{ selectedMeasureName ? ' · ' + selectedMeasureName : '' }}</div>
        <Chart_V5 :chart_data="chartData" :chart_config="chartConfig" override-height="h-[220px]" />
      </div>

      <!-- Filters -->
      <div class="qual-filters">
        <div class="qual-filter-group">
          <label class="qual-filter-label">Care Setting</label>
          <select v-model="careSetting" class="qual-select" style="min-width:100px;">
            <option value="">All</option>
            <option value="SNF">SNF</option>
            <option value="ALF">ALF</option>
          </select>
        </div>
        <div class="qual-filter-group">
          <label class="qual-filter-label">Gap Status</label>
          <select v-model="complianceFilter" class="qual-select" style="min-width:110px;">
            <option value="open">Open Gaps</option>
            <option value="closed">Closed Gaps</option>
            <option value="all">All</option>
          </select>
        </div>
        <div class="qual-filter-group">
          <label class="qual-filter-label">Measure</label>
          <select v-model="selectedMeasure" class="qual-select">
            <option :value="null">All Measures ({{ complianceFilteredRows.length.toLocaleString() }})</option>
            <option v-for="m in measureOptions" :key="m.code" :value="m.code">
              {{ m.name }} ({{ m.count.toLocaleString() }})
            </option>
          </select>
        </div>
        <div class="qual-filter-group">
          <label class="qual-filter-label">Reason</label>
          <select v-model="selectedReason" class="qual-select">
            <option :value="null">All Reasons</option>
            <option v-for="o in reasonOptions" :key="o.name" :value="o.name">
              {{ o.name }} ({{ o.count.toLocaleString() }})
            </option>
          </select>
        </div>
        <div class="qual-filter-group">
          <label class="qual-filter-label">Facility</label>
          <select v-model="selectedFacility" class="qual-select">
            <option :value="null">All Facilities</option>
            <option v-for="f in facilityOptions" :key="f.name" :value="f.name">
              {{ f.name }} ({{ f.count.toLocaleString() }})
            </option>
          </select>
        </div>
        <div class="qual-filter-group">
          <label class="qual-filter-label" style="cursor:pointer;">
            <input type="checkbox" v-model="hideInactive" style="margin-right:4px; vertical-align:middle;" />
            Hide Deceased / Discharged
          </label>
        </div>
        <div class="qual-count">{{ filteredRows.length.toLocaleString() }} patients</div>
      </div>

      <DataTable
        :columns="columns"
        :rows="tableRows"
        row-key="_key"
        max-height="calc(100vh - 300px)"
        saved-view-key="quality-gaps"
        :has-saved="hasSavedView"
        :saved-just-saved="savedViewJustSaved"
        v-model:sort-model="sortModel"
        :export-filename="'quality_gaps_' + (complianceFilter === 'all' ? 'all' : complianceFilter)"
        export-title="Quality Gaps"
        empty-text="No gaps match filters."
        @save-view="saveView"
        @reset-view="resetView"
      >
        <template #cell-_gap="{ row }">
          <span :class="['qual-gap-pill', row._closed ? 'qual-gap-closed' : 'qual-gap-open']">
            {{ row._closed ? 'Closed' : 'Open' }}
          </span>
        </template>
        <template #cell-_pcp="{ row }">
          <div class="sensitive">{{ row.PCP_NAME || '—' }}</div>
          <div class="sensitive" v-if="row.PCP_NPI" style="font-size: 10px; color: #A9A29A; font-family: 'JetBrains Mono', monospace;">{{ row.PCP_NPI }}</div>
        </template>
      </DataTable>
    </div>
  </div>
</template>

<script>
import Chart_V5 from '@/components/widgetcomponents/Viz_Elements/Chart_V5.vue'
import savedView from '@/mixins/savedView'
import { DataTable } from '@/components/External/table'

// Curana "Open X Gaps" composable metrics (STUDIO engine), one per HEDIS measure.
// The chart's open-gap trend is now driven by these metrics (multi-report-date
// time series), not by summarizing the raw patient list. Measure codes match the
// patient list's MEASURE_CODE so the Measure filter maps 1:1.
const QUALITY_METRICS = [
  { id: 649, code: 'CURANA_DPS', name: 'Depression Screening' },
  { id: 650, code: 'CURANA_HTN', name: 'Controlling High Blood Pressure' },
  { id: 651, code: 'CURANA_DM', name: 'Diabetes A1c Control' },
  { id: 652, code: 'CURANA_BCS', name: 'Breast Cancer Screening' },
  { id: 741, code: 'CURANA_CCS', name: 'Colorectal Cancer Screening' },
];

export default {
  name: 'ExternalQuality',
  components: { Chart_V5, DataTable },
  mixins: [savedView],
  savedView: {
    key: 'quality-gaps',
    fields: ['careSetting', 'complianceFilter', 'selectedMeasure', 'selectedReason', 'selectedFacility', 'hideInactive', 'sortModel'],
    scopeProp: 'entityId',
  },
  props: {
    entityId: { type: Number, default: null },
  },
  data() {
    return {
      loading: true,
      allRows: [],
      // metricSeries[metricId] = [{ observation_date, numerator (open gaps), ... }]
      metricSeries: {},
      selectedMeasure: null,
      selectedReason: null,
      selectedFacility: null,
      sortModel: { key: 'patient', dir: 'asc' },
      careSetting: '',
      hideInactive: false,
      complianceFilter: 'open',
      columns: [
        { field: 'patient', header: 'Patient', type: 'text', sticky: true, filterable: true },
        { field: 'BENE_DOB', header: 'DOB', type: 'date' },
        { field: 'PATIENT_IDENTIFIER', header: 'Patient ID', type: 'text', filterable: true },
        { field: 'MEASURE_NAME', header: 'Measure', type: 'text', filterable: true },
        { field: '_gap', header: 'Gap', type: 'text', cell: true, sortable: true, format: (_v, r) => (r._closed ? 'Closed' : 'Open') },
        { field: 'GAP_REASON', header: 'Reason', type: 'text', filterable: true, cellClass: () => 'sensitive', format: v => v || '—' },
        { field: 'GAP_VALUE', header: 'Value', type: 'text', filterable: true, cellClass: () => 'sensitive', format: v => v || '—' },
        { field: 'PATIENT_STATUS', header: 'Status', type: 'text', filterable: true, format: v => v || 'Active' },
        { field: 'FACILITY_NAME', header: 'Facility', type: 'text', filterable: true },
        { field: '_pcp', header: 'PCP', type: 'text', cell: true, format: (_v, r) => r.PCP_NAME || '—' },
        { field: 'LAST_SERVICE_DATE', header: 'Last Service', type: 'date' },
        { field: 'REPORT_DT', header: 'Report Date', type: 'date' },
      ],
    };
  },
  computed: {
    selectedMeasureName() {
      if (!this.selectedMeasure) return '';
      const m = this.measureOptions.find(o => o.code === this.selectedMeasure);
      return m ? m.name : '';
    },
    // Open/closed gap trend, sourced from the composable metrics (649–652/741),
    // NOT summarized from the raw patient list. Each metric is a compliance-rate
    // metric: DENOMINATOR = eligible patients, NUMERATOR = closed (compliant),
    // so OPEN = denominator − numerator. When a measure is selected, show just
    // that metric; otherwise sum all five per report date. Facility/status
    // filters don't apply here (the metric is NPI-aggregated, not patient-level).
    chartData() {
      const metrics = this.selectedMeasure
        ? QUALITY_METRICS.filter(m => m.code === this.selectedMeasure)
        : QUALITY_METRICS;
      const byDate = {};  // date -> { open, closed }
      for (const m of metrics) {
        for (const r of (this.metricSeries[m.id] || [])) {
          const d = (r.observation_date || '').slice(0, 10);
          if (!d) continue;
          const denom = r.denominator || 0;
          const closed = r.numerator || 0;
          if (!byDate[d]) byDate[d] = { open: 0, closed: 0 };
          byDate[d].closed += closed;
          byDate[d].open += Math.max(0, denom - closed);
        }
      }
      const labels = Object.keys(byDate).sort();
      // Both series share offset:0 → Chart_V5 stacks them into one bar per date.
      return {
        labels,
        series: [
          {
            values: labels.map(d => byDate[d].closed),
            config: { label: 'Closed', backgroundColor: 'rgba(247, 103, 52, 0.55)', type: 'bar', yAxis: 'left', offset: 0 },
          },
          {
            values: labels.map(d => byDate[d].open),
            config: { label: 'Open', backgroundColor: 'rgba(169, 162, 154, 0.30)', type: 'bar', yAxis: 'left', offset: 0 },
          },
        ],
      };
    },
    chartConfig() {
      return {
        chartType: 'vertical_bar',
        margin: { top: 24, right: 24, bottom: 36, left: 56 },
        maxBarWidth: 90,
        xAxis: { labelRotation: 0, tickFormat: null, fontSize: 12 },
        yAxes: [
          { position: 'left', tickFormat: null, fontSize: 11, maxHorizontalLabelLength: 40 },
        ],
        legend: { display: true, position: 'top', spacing: 15 },
        formatting: { percentage: false, sig_figs: 0 },
        title: '',
        subtitle: '',
      };
    },
    complianceFilteredRows() {
      if (this.complianceFilter === 'all') return this.allRows;
      const wantClosed = this.complianceFilter === 'closed';
      return this.allRows.filter(r => this.isClosed(r) === wantClosed);
    },
    facilityFilteredRows() {
      if (!this.selectedFacility) return this.complianceFilteredRows;
      return this.complianceFilteredRows.filter(r => (r.FACILITY_NAME || '(none)') === this.selectedFacility);
    },
    measureFilteredRows() {
      if (!this.selectedMeasure) return this.complianceFilteredRows;
      return this.complianceFilteredRows.filter(r => r.MEASURE_CODE === this.selectedMeasure);
    },
    measureOptions() {
      const counts = {};
      for (const r of this.facilityFilteredRows) {
        const code = r.MEASURE_CODE;
        if (!counts[code]) counts[code] = { code, name: r.MEASURE_NAME, count: 0 };
        counts[code].count++;
      }
      return Object.values(counts).sort((a, b) => b.count - a.count);
    },
    facilityOptions() {
      const counts = {};
      for (const r of this.measureFilteredRows) {
        const name = r.FACILITY_NAME || '(none)';
        if (!counts[name]) counts[name] = { name, count: 0 };
        counts[name].count++;
      }
      return Object.values(counts).sort((a, b) => a.name.localeCompare(b.name));
    },
    reasonOptions() {
      const counts = {};
      let rows = this.measureFilteredRows;
      if (this.selectedFacility) rows = rows.filter(r => (r.FACILITY_NAME || '(none)') === this.selectedFacility);
      for (const r of rows) {
        const name = r.GAP_REASON || '(none)';
        if (!counts[name]) counts[name] = { name, count: 0 };
        counts[name].count++;
      }
      return Object.values(counts).sort((a, b) => b.count - a.count);
    },
    filteredRows() {
      let rows = this.complianceFilteredRows;
      if (this.selectedMeasure) rows = rows.filter(r => r.MEASURE_CODE === this.selectedMeasure);
      if (this.selectedReason) rows = rows.filter(r => (r.GAP_REASON || '(none)') === this.selectedReason);
      if (this.selectedFacility) rows = rows.filter(r => (r.FACILITY_NAME || '(none)') === this.selectedFacility);
      if (this.hideInactive) rows = rows.filter(r => {
        const s = (r.PATIENT_STATUS || '').toLowerCase()
        return s !== 'deceased' && s !== 'discharged'
      });
      return rows;
    },
    // Rows fed to DataTable, with the derived display fields precomputed so the
    // columns can sort/filter/export on plain keys (patient name, gap status).
    tableRows() {
      return this.filteredRows.map((r, i) => ({
        ...r,
        _key: (r.PATIENT_IDENTIFIER || 'row') + '|' + (r.MEASURE_CODE || '') + '|' + i,
        patient: this.formatName(r),
        _closed: this.isClosed(r),
        _gap: this.isClosed(r) ? 'Closed' : 'Open',
      }));
    },
  },
  watch: {
    careSetting() { this.fetchData(); },
    entityId() { this.fetchData(); this.fetchMetrics(); },
    measureOptions(opts) {
      if (this.selectedMeasure && !opts.some(o => o.code === this.selectedMeasure)) {
        this.selectedMeasure = null;
      }
    },
    facilityOptions(opts) {
      if (this.selectedFacility && !opts.some(o => o.name === this.selectedFacility)) {
        this.selectedFacility = null;
      }
    },
    reasonOptions(opts) {
      if (this.selectedReason && !opts.some(o => o.name === this.selectedReason)) {
        this.selectedReason = null;
      }
    },
  },
  mounted() { this.fetchData(); this.fetchMetrics(); },
  methods: {
    // Chart data source: each Curana quality metric's open-gap time series.
    async fetchMetrics() {
      try {
        const results = await Promise.all(QUALITY_METRICS.map(m => {
          const payload = { observation_year: 2026, time_series: true, group_by_contract: false };
          if (this.entityId) payload.entity_id = this.entityId;
          else payload.entity_type = 'Tenant';
          return this.$axios
            .post(`/api/impact_analytics/composable-metrics/${m.id}/run/`, payload)
            .then(res => [m.id, res.data?.results || []])
            .catch(() => [m.id, []]);
        }));
        this.metricSeries = Object.fromEntries(results);
      } catch {
        this.metricSeries = {};
      }
    },
    // Called by the mixin's resetView() after it clears storage.
    onResetView() {
      this.careSetting = ''       // watch → refetch
      this.complianceFilter = 'open'
      this.selectedMeasure = null
      this.selectedReason = null
      this.selectedFacility = null
      this.hideInactive = false
      this.sortModel = { key: 'patient', dir: 'asc' }
    },
    formatName(row) {
      if (row.BENE_LAST_NAME && row.BENE_FIRST_NAME) {
        return `${row.BENE_LAST_NAME}, ${row.BENE_FIRST_NAME}`;
      }
      return row.PATIENT_IDENTIFIER || '—';
    },
    isClosed(row) {
      return (row.COMPLIANCE_STATUS || '').toLowerCase() === 'closed gap';
    },
    async fetchData() {
      this.loading = true;
      try {
        const payload = {};
        if (this.entityId) payload.entity_id = this.entityId;
        if (this.careSetting) payload.care_setting = this.careSetting;
        const { data } = await this.$axios.post('/api/portfolio_management/quality-gap-list/', payload);
        this.allRows = data.rows || [];
      } catch {
        this.allRows = [];
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.qual-filters {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
}
.qual-filter-group {
  display: flex;
  align-items: center;
  gap: 6px;
}
.qual-filter-label {
  font-size: 11px;
  font-weight: 500;
  color: #7D7770;
}
.qual-select {
  font-family: inherit;
  font-size: 11.5px;
  color: #4A4540;
  background: #FFFFFF;
  border: 1px solid #E8E3DC;
  border-radius: 5px;
  padding: 5px 8px;
  max-width: 260px;
}
.qual-select:hover {
  border-color: #CFC8BE;
}
.qual-count {
  margin-left: auto;
  font-size: 11.5px;
  font-weight: 500;
  color: #A9A29A;
}
.qual-export-btn {
  font-family: inherit;
  font-size: 11.5px;
  color: #4A4540;
  background: #FFFFFF;
  border: 1px solid #E8E3DC;
  border-radius: 5px;
  padding: 5px 12px;
  cursor: pointer;
}
.qual-export-btn:hover:not(:disabled) {
  border-color: #CFC8BE;
  background: #FAF9F6;
}
.qual-export-btn:disabled {
  opacity: 0.5;
  cursor: default;
}
.qual-chart-card {
  margin-bottom: 20px;
  padding: 16px 18px;
}
.qual-gap-pill {
  display: inline-block;
  font-size: 10px;
  font-weight: 600;
  padding: 1px 8px;
  border-radius: 10px;
  white-space: nowrap;
}
.qual-gap-closed {
  color: #C2410C;
  background: rgba(247, 103, 52, 0.14);
}
.qual-gap-open {
  color: #6B6258;
  background: rgba(169, 162, 154, 0.18);
}
.qual-sort-icon {
  font-size: 9px;
  color: #A9A29A;
  margin-left: 2px;
}
</style>
